import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import { HTMLMotionProps, motion } from 'framer-motion';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';

export enum ButtonVariants {
  WHITE = 'WHITE',
  PRIMARY = 'PRIMARY',
  UNSTYLED = 'UNSTYLED',
  GRAY = 'GRAY',
}

type ButtonSize = 0 | 1 | 2 | 3 | 4 | 5;
type ButtonTheme = 'apprentice' | 'corporate';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size: ButtonSize;
  variant: ButtonVariants;
  theme?: ButtonTheme;
  loading?: boolean;
  rounded?: boolean;
  roundedMd?: boolean;
  roundedSm?: boolean;
  noRadius?: boolean;
  spinColor?: string;
  enableScaling?: boolean;
  shimmer?: boolean;
  shimmerButton?: boolean;
  id?: string;
  icon?: any;
  is3D?: boolean;
  isWidthFull?: boolean;
  roundedUnstyled?: boolean;
  roundedFull?: boolean;
  htmlType?: 'button' | 'submit' | 'reset';
}

export const Spinner = ({ color = 'currentColor' }: { color?: string }) => {
  const screens = useBreakpoint();
  const size = screens.md ? 'h-5 w-5' : 'h-4 w-4';

  return (
    <svg className={`animate-spin ${size} mr-2 md:mr-3`} viewBox='0 0 24 24'>
      <circle
        className='opacity-25'
        cx='12'
        cy='12'
        r='10'
        stroke={color}
        strokeWidth='4'
      ></circle>
      <path
        className='opacity-75'
        fill={color}
        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
      ></path>
    </svg>
  );
};

const ButtonDefault: React.FC<ButtonProps> = ({
  children,
  variant,
  size,
  loading = false,
  rounded = false,
  roundedMd = false,
  roundedSm = false,
  spinColor = 'currentColor',
  enableScaling = true,
  shimmer = false,
  is3D = false,
  className,
  id,
  icon,
  isWidthFull = false,
  shimmerButton = false,
  noRadius,
  roundedUnstyled = false,
  roundedFull = false,
  htmlType = 'button',
  ...props
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [buttonWidth, setButtonWidth] = useState(400);
  const screens = useBreakpoint();

  const commonStyles = {
    [ButtonVariants.WHITE]:
      'text-gray-700 font-medium bg-white border border-gray-300 hover:bg-gray-50 hover:bg-gray-50 active:bg-gray-100 active:border-gray-400 focus-visible:ring-offset-2 focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:outline-none disabled:text-gray-400 disabled:bg-gray-100 disabled:border-gray-200 disabled:cursor-not-allowed',
    [ButtonVariants.GRAY]:
      'text-fadeWhite font-medium bg-[#282829] border border-bgDarkGray hover:bg-dividerColor hover:border-[#444445] active:bg-[#1e1e1f] active:border-[#444445] focus-visible:ring-offset-2 focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:outline-none disabled:text-gray-400 disabled:bg-[#585858] disabled:cursor-not-allowed',
    [ButtonVariants.PRIMARY]:
      'text-white bg-primary border-primary hover:bg-darkPrimary hover:ring-primary active:bg-[#d03000] active:scale-[0.98] font-medium focus-visible:ring-offset-2 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none disabled:text-gray-400 disabled:bg-gray-200 disabled:cursor-not-allowed',
    [ButtonVariants.UNSTYLED]: 'focus-visible:outline-none',
  };

  const commonButtonSizes = {
    '0': ' ',
    '1': 'px-[11px] py-[7px] text-xs',
    '2': 'px-[13px] py-[9px] text-sm',
    '3': 'px-[17px] py-[9px] text-sm',
    '4': 'px-[17px] py-[9px] text-base',
    '5': 'px-[25px] py-[13px] text-base',
  };

  const buttonClasses = clsx(
    commonButtonSizes[size],
    commonStyles[variant],
    className,
    !roundedFull &&
      (noRadius
        ? 'rounded-none'
        : clsx(
            rounded && !roundedUnstyled && 'rounded-xl',
            rounded && roundedMd && !roundedUnstyled && 'lg:rounded-2xl',
            roundedSm && !roundedUnstyled && 'lg:rounded-lg',
            !rounded && !roundedUnstyled && 'rounded-lg',
          )),
    roundedFull && 'rounded-full', // overrides others
    enableScaling &&
      'transition-all duration-150 ease-out transform active:scale-[0.98]',
    'flex justify-center items-center relative overflow-hidden touch-manipulation',
    is3D &&
      'shadow-[0px_-2px_4px_0px_#00000040_inset,0px_3px_2px_0px_#FFFFFF17_inset]',
    is3D &&
      variant === ButtonVariants.PRIMARY &&
      'bg-[radial-gradient(50%_150%_at_46.7%_0%,#FF7837_0%,_#F03906_100%)]',
    isWidthFull && 'w-full',
  );

  const shimmerAnimation = shimmer
    ? {
        animate: {
          backgroundPosition: ['200% 0%', '100% 0%', '-200% 0%'],
        },
        transition: {
          repeat: Number.POSITIVE_INFINITY,
          duration: 3.5,
          ease: 'easeInOut',
        },
      }
    : {};

  useEffect(() => {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth);
    }
  }, []);

  return (
    <motion.button
      ref={buttonRef}
      id={id || undefined}
      className={buttonClasses}
      whileTap={{ scale: enableScaling ? 0.95 : 1 }}
      initial={false}
      type={htmlType}
      transition={{
        type: 'spring',
        stiffness: 500,
        damping: 30,
      }}
      {...(props as React.ComponentPropsWithoutRef<'button'> as HTMLMotionProps<'button'>)}
    >
      <div
        className={clsx(
          'relative z-10 flex items-center justify-center gap-2 no-select',
          isWidthFull ? 'w-full' : '',
          'transition-transform duration-75 cursor-pointer ',
        )}
      >
        {loading && <Spinner color={spinColor} />}
        {!loading && icon && icon}

        {shimmer ? (
          <motion.span
            className='inline-block font-semibold md:px-3 py-1'
            style={{
              display: 'inline-block',
              backgroundImage:
                'linear-gradient(90deg, #000000 0%, #92BDFF 38.5%, #64B5FF 55%, #000000 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              backgroundSize: '200% auto',
            }}
            {...shimmerAnimation}
          >
            {children}
          </motion.span>
        ) : (
          children
        )}
      </div>
      {shimmerButton && (
        <motion.div
          className='absolute pointer-events-none overflow-hidden'
          style={{
            width: 80.88,
            height: 146,
            top: '-20px',
            transform: 'rotate(13.54deg)',
            background: 'white',
            filter: 'blur(40px)',
            zIndex: 10,
            mixBlendMode: 'overlay',
          }}
          initial={{
            left: screens.md ? '-120px' : '-100px',
            opacity: 0,
          }}
          animate={{
            left: [
              screens.md ? '-80px' : '-60px',
              `${buttonWidth + (screens.md ? (screens.xl ? 250 : 100) : 50)}px`,
            ],
            opacity: [0, 1, 1, 0],
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            repeatDelay: 1,
            ease: [0.42, 0, 0.58, 1],
          }}
        />
      )}
    </motion.button>
  );
};

export default ButtonDefault;
